import {
  FileText, Calendar, DollarSign, Clock 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  type Bid, BID_STATUS_LABELS, BID_STATUS_COLORS 
} from '@/types/sourcing';

interface BidCardProps {
  readonly bid: Bid;
  readonly eventTitle?: string;
  readonly onWithdraw?: (bidId: string) => void;
  readonly onViewDetails?: (bidId: string) => void;
}

export function BidCard({
  bid, eventTitle, onWithdraw, onViewDetails 
}: BidCardProps) {
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: bid.currency 
    }).format(amount);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const canWithdraw = bid.status === 'submitted' || bid.status === 'draft';

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base">{eventTitle ?? `Bid ${bid.bidId}`}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {bid.lines.length} items • {formatCurrency(bid.total)}
              </p>
            </div>
          </div>
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${BID_STATUS_COLORS[bid.status]}`}>
            {BID_STATUS_LABELS[bid.status]}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <span>Total: {formatCurrency(bid.total)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span>Valid until {formatDate(bid.validUntil)}</span>
          </div>
          {bid.submittedAt && (
            <div className="flex items-center gap-2 col-span-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Submitted {formatDate(bid.submittedAt)}</span>
            </div>
          )}
        </div>

        {bid.notes && (
          <p className="text-sm text-muted-foreground border-t pt-3">{bid.notes}</p>
        )}

        <div className="flex gap-2 pt-2">
          {canWithdraw && onWithdraw && (
            <Button variant="outline" size="sm" onClick={() => onWithdraw(bid.bidId)}>
              Withdraw
            </Button>
          )}
          {onViewDetails && (
            <Button variant="outline" size="sm" onClick={() => onViewDetails(bid.bidId)}>
              View Details
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
